import { useState, useEffect, useRef } from 'react';
import { X, Shield } from '@phosphor-icons/react';
import { Button, Input } from '@t-one-internal/design-system';
import { QRCodeSVG } from 'qrcode.react';
import {
  initiateTwoFactorSetup,
  completeTwoFactorSetup,
  resendSetupEmailOTP,
  initiatePublicTwoFactorSetup,
  completePublicTwoFactorSetup,
  resendEmailOTP,
} from '../services/twoFactorApi';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSetupComplete: () => void;
  userEmail?: string;
  /** Session ID for admin-enforced setup flow (uses public endpoints) */
  sessionId?: string;
  /** When true, user cannot close/cancel the modal */
  isEnforced?: boolean;
  /** Called on successful enforced setup with tokens from backend */
  onEnforcedSetupComplete?: (data: { access_token: string; refresh_token: string; user: any }) => void;
}

type OTPMethod = 'totp' | 'email';
type Step = 'method-select' | 'loading' | 'qr' | 'email-verify' | 'complete';

interface SetupData {
  secret: string;
  qrCode: string;
}

export default function TwoFactorSetupModal({
  isOpen,
  onClose,
  onSetupComplete,
  userEmail,
  sessionId,
  isEnforced,
  onEnforcedSetupComplete,
}: TwoFactorSetupModalProps) {
  const [step, setStep] = useState<Step>('method-select');
  const [selectedMethod, setSelectedMethod] = useState<OTPMethod | null>(null);
  const [setupData, setSetupData] = useState<SetupData | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [emailAddress, setEmailAddress] = useState(userEmail || '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('method-select');
      setSelectedMethod(null);
      setSetupData(null);
      setCode('');
      setError('');
      setLoading(false);
      setResendSuccess(false);
      setIsResending(false);
    }
  }, [isOpen]);

  // Focus code input when verification steps are shown
  useEffect(() => {
    if ((step === 'qr' || step === 'email-verify') && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [step]);

  const handleSelectMethod = async (method: OTPMethod) => {
    setSelectedMethod(method);
    setStep('loading');
    setError('');

    try {
      let response;
      if (sessionId) {
        // Enforced flow: use public session-based endpoint
        response = await initiatePublicTwoFactorSetup(sessionId);
      } else {
        // Self-service flow: use authenticated endpoint
        response = await initiateTwoFactorSetup(true, method);
      }

      if (response.success) {
        // Detect method from response (backend may override for enforced flow)
        const resolvedMethod = sessionId
          ? ((response.data?.otp_method || response.data?.otp_preference || method) === 'email' ? 'email' : 'totp') as OTPMethod
          : method;

        if (sessionId && resolvedMethod !== method) {
          setSelectedMethod(resolvedMethod);
        }

        if (resolvedMethod === 'totp' && response.data) {
          setSetupData({
            secret: response.data.secret || '',
            qrCode: response.data.qr_code || '',
          });
          setStep('qr');
        } else if (resolvedMethod === 'email') {
          if (response.data?.user_email) {
            setEmailAddress(response.data.user_email);
          }
          setStep('email-verify');
        }
      } else {
        setError(response.message || 'Failed to initiate setup');
        setStep('method-select');
      }
    } catch (err) {
      console.error('Failed to initiate 2FA setup:', err);
      setError(err instanceof Error ? err.message : 'Failed to initiate setup. Please try again.');
      setStep('method-select');
    }
  };

  const handleVerifyCode = async () => {
    if (code.length !== 6) return;

    setLoading(true);
    setError('');
    try {
      const secret = selectedMethod === 'totp' ? (setupData?.secret || '') : '';

      if (sessionId) {
        // Enforced flow: use public session-based endpoint
        const response = await completePublicTwoFactorSetup(
          sessionId,
          secret,
          code,
          selectedMethod || 'totp'
        );
        if (response.success) {
          setStep('complete');
          setTimeout(() => {
            if (onEnforcedSetupComplete) {
              onEnforcedSetupComplete({
                access_token: response.access_token,
                refresh_token: response.refresh_token,
                user: response.user,
              });
            }
          }, 1500);
        } else {
          setError(response.message || 'Invalid verification code. Please try again.');
        }
      } else {
        // Self-service flow: use authenticated endpoint
        const response = await completeTwoFactorSetup(secret, code, true, selectedMethod || 'totp');
        if (response.success) {
          setStep('complete');
          setTimeout(() => {
            onSetupComplete();
          }, 1500);
        } else {
          setError(response.message || 'Invalid verification code. Please try again.');
        }
      }
    } catch (err) {
      console.error('Failed to verify code:', err);
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setIsResending(true);
    setError('');
    setResendSuccess(false);
    try {
      let response;
      if (sessionId) {
        // Enforced flow: use public session-based endpoint
        response = await resendEmailOTP(sessionId);
      } else {
        // Self-service flow: use authenticated endpoint
        response = await resendSetupEmailOTP();
      }
      if (response.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        setError(response.message || 'Failed to resend code');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  if (!isOpen) return null;

  // Mask email for display: show first 3 chars + ***@domain
  const maskedEmail = emailAddress
    ? emailAddress.replace(/^(.{3})(.*)(@.+)$/, '$1***$3')
    : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" weight="fill" />
            <h2 className="text-lg font-semibold text-gray-900">
              Set Up Two-Factor Authentication
            </h2>
          </div>
          {!isEnforced && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
              disabled={loading}
            >
              <X size={20} />
            </button>
          )}
        </div>

        {/* Step Indicator */}
        {step !== 'complete' && (
          <div className="px-6 pt-4 flex items-center gap-2">
            {['method-select', selectedMethod === 'totp' ? 'qr' : 'email-verify', 'complete'].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  (step === 'method-select' && i === 0) ||
                  ((step === 'qr' || step === 'email-verify' || step === 'loading') && i === 1)
                    ? 'bg-blue-600'
                    : 'bg-gray-200'
                }`} />
                {i < 2 && <div className="w-8 h-px bg-gray-200" />}
              </div>
            ))}
          </div>
        )}

        <div className="p-6">
          {/* Step 1: Method Selection */}
          {step === 'method-select' && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  Choose how you'd like to verify your identity during login.
                </p>
                <p className="text-xs text-gray-400">
                  You'll need this on every login going forward.
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Authenticator App Option */}
              <button
                type="button"
                onClick={() => handleSelectMethod('totp')}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/30 transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">Authenticator App</span>
                      <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Recommended</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Use Google Authenticator, Authy, or similar apps. More secure and works offline.
                    </p>
                  </div>
                </div>
              </button>

              {/* Email OTP Option */}
              <button
                type="button"
                onClick={() => handleSelectMethod('email')}
                className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/30 transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-gray-200 transition-colors">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-900">Email Verification</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Receive a 6-digit code via email each time you log in. Simpler, no app needed.
                    </p>
                  </div>
                </div>
              </button>

              {!isEnforced && (
                <div className="pt-2">
                  <Button type="button" variant="outlined" onClick={onClose} className="w-full">
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Loading State */}
          {step === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-sm text-gray-600">
                {selectedMethod === 'email' ? 'Sending verification code...' : 'Generating QR code...'}
              </p>
            </div>
          )}

          {/* Step 2a: TOTP — QR Code + Verification */}
          {step === 'qr' && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">
                Scan the QR code below with your authenticator app, then enter the 6-digit code.
              </p>

              {setupData && (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg border-2 border-gray-100">
                    <QRCodeSVG value={setupData.qrCode} size={200} level="H" />
                  </div>
                  <div className="w-full mt-4">
                    <p className="text-xs text-gray-500 mb-1 text-center">
                      Can't scan? Enter this code manually:
                    </p>
                    <div className="bg-gray-50 px-3 py-2 rounded font-mono text-xs text-center break-all select-all border">
                      {setupData.secret}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-800 mb-1">Instructions:</p>
                <ol className="list-decimal list-inside text-xs text-blue-700 space-y-0.5">
                  <li>Open your authenticator app</li>
                  <li>Scan the QR code or enter the code manually</li>
                  <li>Enter the 6-digit code from the app below</li>
                </ol>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter 6-digit verification code
                </label>
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  disabled={loading}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && code.length === 6) {
                      handleVerifyCode();
                    }
                  }}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => { setStep('method-select'); setCode(''); setError(''); setSetupData(null); }}
                  disabled={loading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="flex-1"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify & Enable'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2b: Email OTP — Verification */}
          {step === 'email-verify' && (
            <div className="space-y-5">
              <div>
                <p className="text-sm text-gray-600">
                  We've sent a 6-digit verification code to your email
                  {maskedEmail ? <span className="font-medium"> ({maskedEmail})</span> : ''}.
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  The code expires in 5 minutes.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter 6-digit verification code
                </label>
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  disabled={loading}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && code.length === 6) {
                      handleVerifyCode();
                    }
                  }}
                />
              </div>

              {/* Resend */}
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResendEmail}
                    disabled={isResending}
                    className="text-blue-600 hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? 'Resending...' : 'Resend code'}
                  </button>
                </p>
                {resendSuccess && (
                  <p className="text-xs text-green-600 mt-1">New code sent! Check your email.</p>
                )}
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => { setStep('method-select'); setCode(''); setError(''); }}
                  disabled={loading}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={handleVerifyCode}
                  disabled={loading || code.length !== 6}
                  className="flex-1"
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </div>
                  ) : (
                    'Verify & Enable'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2FA Enabled Successfully!</h3>
              <p className="text-sm text-gray-600">
                {selectedMethod === 'email'
                  ? 'Email verification is now active on your account.'
                  : 'Authenticator app verification is now active on your account.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
